import WBSRowModel from "./WBSRowModel";
import {
  ICustomProperties,
  IRowMeasured,
  IRowMeasuredDetails,
} from "../interfaces";
import { decimal, sum, valueToPercentual } from "../../utils/DecimalPrecision";
import { ERowStatus } from "api/enums";

/**
 * Model de Linha do Contrato
 * @category Contracts
 * @hideconstructor
 */

class ContractRowModel extends WBSRowModel {
  /** ID do contrato. */
  public readonly contractUid: string;
  /** Código estruturado da etapa pai. */
  public readonly parentStructured: string;
  /** Descrição da linha. */
  public description: string;
  /** ID da etapa pai. */
  public readonly parentStage: string;
  /** Índice da linha. */
  public readonly rowIndex: number;
  /** Unidade do insumo. */
  public unit: string;
  /** Total e quantidade total medidos para a linha. */
  private measured: IRowMeasured;
  /** Detalhes dos valores medidos na linha. Separando cada total e quantidade
   * por medição.
   */
  private measuredDetails: IRowMeasuredDetails;
  /** Data de criação. */
  public readonly createdAt: Date;
  /** Data de modificação. */
  public modifiedAt: Date;
  /** Status da linha. Usado para verificar se esta sendo alterada ou não. */
  public status: ERowStatus;
  /** Propriedades customizadas da linha. */
  public customProperties?: ICustomProperties;

  constructor(
    uid: string,
    contractUid: string = "",
    structured: string = "",
    parentStructured: string = "",
    description: string = "",
    parentStage: string = "-1",
    rowIndex: number = 0,
    rowType: number = 0,
    quantity: number = 0,
    unit: string = "",
    unitCost: number = 0,
    total: number = 0,
    measuredDetails: IRowMeasuredDetails = {},
    status: ERowStatus = ERowStatus.UNCHAGED,
    customProperties: ICustomProperties = {},
    createdAt: Date = new Date(),
    modifiedAt: Date = new Date()
  ) {
    super(uid, structured, rowType, quantity, unitCost, total);

    this.contractUid = contractUid;
    this.parentStructured = parentStructured;
    this.description = description;
    this.parentStage = parentStage;
    this.rowIndex = rowIndex;
    this.rowType = rowType;
    this.unit = unit;
    this.measuredDetails = measuredDetails;
    this.measured = this.sumMeasureds();
    this.status = status;
    this.customProperties = customProperties;
    this.createdAt = createdAt;
    this.modifiedAt = modifiedAt;
  }

  /**
   * Obtém a cópia do objeto com os totais dos valores
   * medidos para linha nas medições.
   */
  getMeasured(): IRowMeasured {
    return this.isStage
      ? {
          total: this.measured.total,
        }
      : { ...this.measured };
  }

  /** Retorna se a linha do contrato possui valores medidos ou não. */
  hasMeasuredValues(): boolean {
    return Boolean(Object.keys(this.measuredDetails).length);
  }

  /** Obtém o saldo de quantidade da linha, que é o valor que
   * ainda não foi medido.
   */
  getBalanceQuantity(): number {
    if (this.isStage) return 0;

    return decimal(this.quantity)
      .subtract(this.measured.quantity || 0)
      .toNumber();
  }

  /** Obtém o percentual do total da linha em relação ao total do contrato. */
  getPercentual(totalContract: number): number {
    return valueToPercentual(this.total, totalContract);
  }

  /** Verifica se a linha pode ser exibida na tabela informada.
   * As linhas distratadas e sem medição, não devem aparecer na tabela da
   * tela de medição.
   */
  canShowAt(page: "contract" | "measurement"): boolean {
    if (page === "contract") return true;
    const isACanceledResource =
      this.status === ERowStatus.CANCELED && this.measured.total === 0;
    return !isACanceledResource;
  }

  /** Adiciona os valores medido na medição informada nos detalhes da linha
   * e atualiza os totais medidos da mesma.
   */
  setMeasuredOf(measurementId: string, total: number, quantity: number = 0) {
    if (!measurementId) return;

    this.measuredDetails[measurementId] = {
      total: decimal(total, 2).setToMaxOrMin(this.total, 0),
      quantity: decimal(quantity).setToMaxOrMin(this.quantity, 0),
    };
    this.measured = this.sumMeasureds();
  }

  /** Remove os valores medido na medição informada dos detalhes da linha
   * e atualiza os totais medidos da mesma.
   */
  removeMeasuredOf(measurementId: string) {
    if (!measurementId && this.measuredDetails[measurementId]) return;

    const newDetaisl = { ...this.measuredDetails };
    delete newDetaisl[measurementId];

    this.measuredDetails = newDetaisl;
    this.measured = this.sumMeasureds();
  }

  /** Soma os valores medidos de cada medição incluída no campo measuredDetails
   * e retornar o objeto com o total medido e quantidade total medida.
   */
  private sumMeasureds() {
    let quantityMeasured = 0;
    let totalMeasured = 0;

    Object.values(this.measuredDetails).forEach(({ quantity, total }) => {
      quantityMeasured = sum(quantityMeasured, quantity || 0);
      totalMeasured = sum(totalMeasured, total || 0);
    });

    return {
      quantity: quantityMeasured,
      total: totalMeasured,
    };
  }

  /** Monta o objeto JSON com os dados que serão persistidos no Firebase. */
  toSave() {
    return {
      structured: this.structured,
      parentStructured: this.parentStructured,
      description: this.description,
      parentStage: this.parentStage,
      rowIndex: this.rowIndex,
      rowType: this.rowType,
      quantity: this.quantity,
      unit: this.unit,
      unitCost: this.unitCost,
      total: this.total,
      status: this.status,
      measured: { ...this.measured },
      measuredDetails: { ...this.measuredDetails },
      createdAt: this.createdAt,
      modifiedAt: this.modifiedAt,
    };
  }

  /** Cria uma instância de ContractRowModel a partir do objeto JSON passado por parâmetro. */
  static create(
    uid: string,
    data: Partial<ContractRowModel> & {
      measuredDetails?: IRowMeasuredDetails;
    }
  ): ContractRowModel {
    return new ContractRowModel(
      uid,
      data.contractUid,
      data.structured,
      data.parentStructured,
      data.description,
      data.parentStage,
      data.rowIndex,
      data.rowType,
      data.quantity,
      data.unit,
      data.unitCost,
      data.total,
      data.measuredDetails,
      data.status,
      data.customProperties,
      data.createdAt,
      data.modifiedAt
    );
  }
}

export default ContractRowModel;
